import type { Middleware } from 'redux';
import { omit } from 'underscore';
import isEqual from 'fast-deep-equal';

import { SET_LOADING_EVENT_BUNDLE, UPDATE_EVENT_BUNDLE } from '../actions/event/event-actions';
import type { EventsState } from '../types';
import type { Action } from '../../types/actions';

// Socket bundle updates re-dispatch the full event bundle on every message, even when nothing
// changed. This middleware (1) drops no-op updates so the eventBundle reference doesn't churn —
// otherwise registration effects keyed on it (getTicketQuantities, Stripe payment-intent setup)
// re-fire on every socket tick — and (2) on a registration-structure change, collapses the view
// to <Loader/> first so the registration subtree (Stripe/ticketing) remounts cleanly on the new
// structure instead of reconciling mid-flight.

const bundleUpdateMiddleware: Middleware<{}, EventsState> = store => next => action => {
	const typedAction = action as Action;
	if (typedAction.type !== UPDATE_EVENT_BUNDLE) {
		return next(action);
	}

	const { eventBundle: prev } = store.getState().LiveEventReducer;
	const newBundle = typedAction.payload;

	const IGNORED_FIELDS = ['created_date'] as const;

	// skip the dispatch entirely if nothing meaningful changed
	if (isEqual(omit(prev ?? {}, ...IGNORED_FIELDS), omit(newBundle, ...IGNORED_FIELDS))) {
		return;
	}

	// compare the number of enabled registration steps between the current and incoming bundle
	// an increase means new registration components (e.g. Stripe/ticketing) are about to mount
	const prevSteps = prev?.registration_steps?.filter((s: { isOn: boolean }) => s.isOn).length ?? 0;
	const nextSteps = newBundle?.registration_steps?.filter((s: { isOn: boolean }) => s.isOn).length ?? 0;
	const structureChanged = prev?.registration_on !== newBundle?.registration_on || prevSteps !== nextSteps;

	if (structureChanged) {
		// React 19 auto-batches both dispatches into one render
		// loadingEventBundle=true and collapses to <Loader/>, unmounting all subscribers
		// before useLayoutEffect fires — preventing the forceUpdate cascade
		next({ type: SET_LOADING_EVENT_BUNDLE, payload: null });
		next(action);
	} else {
		next(action);
	}
};

export default bundleUpdateMiddleware;
